import React, { FC, memo, useMemo } from 'react';
import { useIntl } from 'umi';
import AceEditor from 'react-ace';
import { MainContentCmp } from '../SubComp';
import 'ace-builds/src-noconflict/mode-javascript';
import 'ace-builds/src-noconflict/mode-typescript';
import 'ace-builds/src-noconflict/mode-python';
import 'ace-builds/src-noconflict/mode-java';
import 'ace-builds/src-noconflict/mode-sql';
import 'ace-builds/src-noconflict/mode-mysql';
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/mode-html';
import 'ace-builds/src-noconflict/mode-css';
import 'ace-builds/src-noconflict/mode-markdown';
import 'ace-builds/src-noconflict/theme-github';
import TableActions from '../TableComponents/TableActions';
import { useCodeActions } from './hooks';
import { Collapse } from '@/pages/aigc/components/Collapse';
import AgentCodeIcon from '@/pages/aigc/assets/svg/agent-code-icon.svg';
import './index.scss';

/**
 * Markdown节点接口
 */
interface IMarkdownNode {
  tagName: string;
  children?: any[];
}

/**
 * CodeWrapper组件的Props接口
 * @description 代码块包装组件的属性定义
 */
interface ICodeWrapperProps {
  /** Markdown节点信息 */
  node?: IMarkdownNode;

  /**
   * 是否为内联代码
   * @description
   * - 内联代码：指在文本段落中的代码片段，如 `const a = 1`，用单个反引号包裹
   * - 代码块：指独立的多行代码区域，用三个反引号包裹
   * - 当 inline=true 时，直接渲染为简单的 <code> 标签
   * - 当 inline=false 时，使用 Ace Editor 进行语法高亮显示
   */
  inline?: boolean;

  /**
   * 类名（包含语言信息）
   * @description
   * - Markdown 会自动为代码块添加 'language-xxx' 格式的类名
   * - 例如：```javascript 会生成 className="language-javascript"
   * - 组件通过正则匹配提取语言类型
   */
  className?: string;

  /**
   * 子元素（代码内容）
   * @description
   * - 通常是字符串数组，包含实际的代码文本
   */
  children?: any[];

  /**
   * 来源标识
   * @description 用于标识代码块的来源，可用于日志追踪或特殊处理
   */
  from?: string;

  /**
   * 其他属性（会直接传递给 AceEditor）
   * @description
   * - 可以传递任何 AceEditor 支持的属性来覆盖默认配置
   * - 例如：theme, fontSize, minLines, maxLines, showLineNumbers 等
   */
  [key: string]: any;
}



/**
 * 验证是否有语言标识
 */
const hasLanguageClass = (className?: string): boolean => {
  if (!className) return false;
  return /language-(\w+)/.test(className);
};

/**
 * 代码块包装组件
 * @description 为代码块提供统一的包装和操作功能，支持语法高亮、复制、下载、折叠/展开
 */
const CodeWrapper: FC<ICodeWrapperProps> = ({
  node,
  inline,
  className,
  children,
  ...restProps
}) => {
  // 折叠状态、操作配置、代码内容等统一由 hook 提供
  const { codeActions, codeValue, language, aceMode, collapsed, lineCount, handleEditorLoad } = useCodeActions({
    children,
    className,
  });
  const { formatMessage } = useIntl();

  // 代码块：验证是否有语言标识
  const hasLanguage = useMemo(() => {
    return hasLanguageClass(className);
  }, [className]);

  // 行内代码直接返回 code 标签
  if (inline) {
    return (
      <code className={className} {...restProps}>
        {children}
      </code>
    );
  }

  // 无语言标识的代码块，按普通文本渲染
  if (!hasLanguage) {
    return (
      <MainContentCmp
        className="dba-code-wrapper-markdown-body"
        value={children?.toString()}
      />
    );
  }

  return (
    <div className={`dba-code-wrapper${collapsed ? ' dba-code-wrapper--collapsed' : ''}`}>
      <div className="dba-code-wrapper-header">
        <span className="dba-code-wrapper-language">
          <img src={AgentCodeIcon} alt="" />
          {language || 'text'}
        </span>
        <TableActions
          actions={codeActions}
        />
      </div>
      <Collapse
        className="dba-code-wrapper-content collapse-css-transition"
        isOpen={!collapsed}
        overflowOnExpanded
      >
        <AceEditor
          mode={aceMode}
          value={codeValue}
          width="100%"
          height="auto"
          theme="github"
          fontSize={14}
          minLines={3}
          maxLines={Infinity}
          readOnly
          showPrintMargin={false}
          showGutter
          wrapEnabled
          highlightActiveLine={false}
          onLoad={handleEditorLoad}
          setOptions={{
            useWorker: false,
            showLineNumbers: true,
            tabSize: 2,
            showFoldWidgets: false,
          }}
          {...restProps}
        />
      </Collapse>
      {collapsed && (
        <div className="dba-code-wrapper-collapsed-placeholder">
          {formatMessage({ id: 'dba_code_collapsed_lines' }, { lines: lineCount })}
        </div>
      )}
    </div>
  );
};

/**
 * Props比较函数
 * @description 用于memo的第二个参数，只比较children内容是否一致，避免不必要的重渲染
 */
const arePropsEqual = (prevProps: ICodeWrapperProps, nextProps: ICodeWrapperProps): boolean => JSON.stringify(prevProps.children) === JSON.stringify(nextProps.children);

export default memo(CodeWrapper, arePropsEqual);
